import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
  ChangeDetectorRef  
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, of, from } from 'rxjs';
import { catchError } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { Documents } from 'src/app/models/documents.models';
import { Orders } from 'src/app/models/Orders.model';
import { Status } from 'src/app/models/status';
import { IWsResult } from 'src/app/models/ws_result.model';
import { OrdersService } from 'src/app/services/orders.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';


/**
 * Sub-modale "Conferma scatole" — terzo step del wizard picking.
 * Aperto via `OpenModalService.openCheckBox()` da preparazione-ordini-detaggli J.3.
 *
 * Cleanup v2.11.0 J.4.a:
 * - 🚨 FIX BUG: `bigRefresh()` faceva `loading = !loading` toggle (potenziale
 *   stale state se chiamato 2× di seguito). Ora `loading = true` esplicito.
 * - 🚨 FIX BUG: nested subscribes in reloadFunction (getOrder1 → getDocuments
 *   → getOrderDocuments) → `forkJoin` per parallelo (memory leak fix).
 * - 🚨 FIX dead: @ViewChild(GridComponent), GridComponent import — mai usato.
 * - 🚨 FIX dead: `lodash` import (mai usato sotto).
 * - 🚨 FIX dead: `sortChange`, `onToggle`, `show`, `toggleText`, `isEditable`
 *   — mai chiamati dal template.
 * - 🚨 FIX dead: `goBackFunction`, `orderBoxesByName` — mai chiamati.
 * - Switch a inject() + takeUntilDestroyed.
 * - Drop ~15 console.log (incluso "ORDERUS IS", "BOX OBX", "Pow pow",
 *   "FILTERED PRODUCTS", "MANANC CACAT" 🇷🇴 in list-of-products).
 * - Drop kendo-dialog + kendo-label + mat-spinner refs.
 * - 🚨 FIX RENDER: aggiunto ChangeDetectorRef per force detection dopo subscribe.
 */
@Component({
  selector: 'app-confirm-boxes',
  templateUrl: './confirm-boxes.component.html',
  styleUrls: ['./confirm-boxes.component.scss'],
  standalone: false,
})

export class ConfirmBoxesComponent implements OnInit {
  @Input() openedBox = true;
  @Output() modalClosed1 = new EventEmitter<void>();


  // === DI ===
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private orderService = inject(OrdersService);
  public closeServ = inject(OpenModalService);
  private cdRef = inject(ChangeDetectorRef);

  // === State ===
  public order: Orders.Order | null = null;
  public orderToUpdate: Orders.OrderUpdate = new Orders.OrderUpdate(null);
  public documents: Documents.Document[] = [];

  public id = '';
  public boxNu = 0;
  public clickedBox: any = null;
  public loading = false;
  public firstEnteredOnPage = true;

  // Selezione corrente per app-list-of-products-for-boxes
  public selectedBoxToAddProducts: any = null;
  public orderToPutInBox: any = null;


  // === Lifecycle ===

  ngOnInit(): void {
    this.bigRefresh();
  }

  bigRefresh(): void {
    this.loading = true;
    this.id = this.route.snapshot.paramMap.get('id') ?? '';
    this.reloadFunction();
  }

  close(): void {
    this.closeServ.closeCheckBox();
    this.closeServ.isColorConfirmed2();
  }

  private afterLoad(): void {
    this.loading = false;
    this.cdRef.detectChanges();
  }


  /**
   * 🚨 FIX BUG: legacy aveva 3 subscribe annidate (getOrder1 → getDocuments
   * → getOrderDocuments) senza takeUntilDestroyed. Memory leak.
   * Ora forkJoin parallelo + takeUntilDestroyed cleanup.
   */
  reloadFunction(): void {

    if (!this.id) {
      this.loading = false;
      this.cdRef.detectChanges();  
      return;
    }

    this.loading = true;

    forkJoin({
      order: this.orderService.getOrder1(+this.id).pipe(
        catchError(() => of(null))
      ),
      documents: this.orderService.getDocuments(+this.id).pipe(
        catchError(() => of([]))
      ),
      enriched: from(
        this.orderService.getOrderDocuments(+this.id)
      ).pipe(
        catchError(() => of([]))
      ),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ order, documents, enriched }) => {
        if (!order) {
          this.loading = false;
          this.cdRef.detectChanges(); 
          return;
        }

        const fixedOrder = new Orders.Order(order as any);

        fixedOrder.boxes = (order.boxes ?? []).map((b: any) => ({
          ...b,
          isDeleted: false,
          ts_documents: 'N',
          ts_screws: 'N',
        }));


        this.order = fixedOrder;
        this.orderToUpdate = new Orders.OrderUpdate(fixedOrder);

        if (this.orderToUpdate.or_carrier_id === 0) {
          this.orderToUpdate.or_carrier_id = null;
        }

        this.documents =
          (enriched && (enriched as any[]).length)
            ? (enriched as any[])
            : documents;

        this.loading = false;
        this.cdRef.detectChanges();  
      });
  }


  // === Box actions ===

  showBoxDetails(box: any, boxNo: number): void {
    this.clickedBox = box;
    this.boxNu = boxNo;
    this.closeServ.openDetBox();
  }

  goToBox(boxTypeId: any): void {
    this.clickedBox = boxTypeId;
    this.closeServ.openBox();
  }

  removeBox(id: number): void {
    this.loading = true;
    this.orderToUpdate.boxes[id].isDeleted = true;
    this.orderService
      .updateOrder(this.orderToUpdate)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        if (data?.success) {
          Swal.fire({
            icon: 'success',
            title: 'Rimossa!',
            text: 'La scatola è stata rimossa con successo.',
            timer: 1500,
            timerProgressBar: true,
          });
          this.bigRefresh();
        } 
        else {
          this.loading = false;
          this.cdRef.detectChanges();  
        }
      });
  }

  addBox(dataInfo: any): void {
    for (let i = 0; i < dataInfo.boxesNo; i++) {
      this.orderToUpdate.boxes.push({
        ts_box_type_id: dataInfo.normal.box_type_id,
        ts_box_type_desc: dataInfo.normal.box_type_desc,
        ts_box_height: dataInfo.custom.ts_box_height,
        ts_box_length: dataInfo.custom.ts_box_length,
        ts_box_depth: dataInfo.custom.ts_box_depth,
        ts_box_weight: dataInfo.custom.ts_box_weight,
        ts_documents: 'N',
        box_order_id: null,
        ts_screws: 'N',
        isDeleted: dataInfo.normal.isDeleted,
      } as any);
    }
    this.orderService
      .updateOrder(this.orderToUpdate)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        if (data?.success) this.bigRefresh();
      });
  }

  updateCustomBox(dataInfo: any): void {
    this.orderToUpdate.boxes[dataInfo.boxNo] = {
      ts_box_type_id: null,
      ts_box_type_desc: '',
      ts_box_height: dataInfo.custom.ts_box_height,
      ts_box_length: dataInfo.custom.ts_box_length,
      ts_box_depth: dataInfo.custom.ts_box_depth,
      ts_box_weight: dataInfo.custom.ts_box_weight,
      ts_documents: 'N',
      ts_screws: 'N',
      isDeleted: dataInfo.custom.isDeleted,
    } as any;
    this.orderService
      .updateOrder(this.orderToUpdate)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.cdRef.detectChanges(); 
      });
  }

  addDocumentsToBox(boxNo: number): void {
    const current = this.orderToUpdate.boxes[boxNo].ts_documents;
    this.orderToUpdate.boxes[boxNo].ts_documents =
      current?.toUpperCase() === 'S' ? 'N' : 'S';
    this.orderService
      .updateOrder(this.orderToUpdate)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.cdRef.detectChanges(); 
      });
  }

  addScrewsToBox(boxNo: number): void {
    const current = this.orderToUpdate.boxes[boxNo].ts_screws;
    this.orderToUpdate.boxes[boxNo].ts_screws =
      current?.toUpperCase() === 'S' ? 'N' : 'S';
    this.orderService
      .updateOrder(this.orderToUpdate)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.cdRef.detectChanges(); 
      });
  }


  // === Print labels ===

  printAllLabels(): void {
    if (!this.order) return;
    this.orderService
      .getAllBoxLabels(
        this.order.order_id,
        this.order.boxes.map((_, index) => index + 1),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result?.box_label_link_pdf) return;
        window.open(result.box_label_link_pdf);
      });
  }


  // === Documents check ===

  checkDocument(id: number): void {
    this.documents[id].checked = !this.documents[id].checked;
    this.documents.sort(
      (a, b) =>
        +(a.checked ? a.checked : false) - +(b.checked ? b.checked : false),
    );
    this.cdRef.detectChanges(); 
  }


  // === Save step ===

  saveStep(pageName: string): void {
    if (!this.order) return;
    let statusNo = 0;
    if (pageName === 'boxes') statusNo = Status.StatusesUser.step3;

    this.orderService
      .setStatus(this.order.order_id, statusNo)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((_res: IWsResult) => {
        this.orderService
          .updateOrder(this.orderToUpdate)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.modalClosed1.emit();
            this.close();
            Swal.fire({
              icon: 'success',
              title: 'Aggiunte!',
              text: 'Colli selezionati con successo.',
              timer: 1500,
              timerProgressBar: true,
            });
            this.cdRef.detectChanges(); 
          });
      });
  }


  // === List of products for box (apre sub-modale) ===

  async seeListOfProducts(box: any): Promise<void> {
    if (!this.order) return;
    this.selectedBoxToAddProducts = box;

    const filteredProducts = await this.filterProducts();
    this.orderToPutInBox = {
      ...this.order,
      products: filteredProducts,
    };

    this.closeServ.onListOfProductsBoxes();
    this.cdRef.detectChanges();  
  }


  /**
   * Filtra i prodotti dell'ordine includendo solo quelli con barcodes
   * disponibili (non assegnati a un'altra box) e/o sub-products con
   * barcodes disponibili.
   */
  private filterProducts(): Promise<any[]> {
    return new Promise((resolve) => {
      if (!this.order) {
        resolve([]);
        return;
      }
      const filtered = (this.order.products ?? [])
        .map((prod) => {
          const hasValidBarcodes = prod.barcodes && prod.barcodes.length > 0;
          const hasSubProducts = prod.sub_products && prod.sub_products.length > 0;

          if (hasValidBarcodes && !hasSubProducts) {
            prod.barcodes = prod.barcodes.filter(
              (b: any) =>
                b.box_id === null ||
                b.box_id === this.selectedBoxToAddProducts?.box_order_id,
            );
            return prod.barcodes.length > 0 ? prod : null;
          }
          if (!hasValidBarcodes && hasSubProducts) {
            prod.sub_products = prod.sub_products.filter((sub: any) => {
              sub.barcodes = sub.barcodes.filter(
                (b: any) =>
                  b.box_id === null ||
                  b.box_id === this.selectedBoxToAddProducts?.box_order_id,
              );
              return sub.barcodes.length > 0;
            });
            return prod.sub_products.length > 0 ? prod : null;
          }
          return null;
        })
        .filter(Boolean);
      resolve(filtered);
    });
  }


  // === Helpers ===

  isBoxScrewsChecked(box: any): boolean {
    return box?.ts_screws === 'S';
  }
  isBoxDocumentsChecked(box: any): boolean {
    return box?.ts_documents === 'S';
  }
  
}